import React from 'react';
import ViewSourceIcon from '../icons/ViewSourceIcon';
import './SourceChatPanelList.css';

export interface SourceItem
{
  source_type: 'document' | 'project' | 'global' | 'web' | 'api';
  source_id: string;
  content: string;  // Preview (first 200 chars)
  metadata?: {
    // For documents:
    filename?: string;
    page_number?: number;
    section?: string;
    confidence?: number;

    // For web:
    url?: string;
    title?: string;
    domain?: string;

    // For API:
    api_name?: string;
    endpoint?: string;
    timestamp?: string;
  };
}

interface SourceChatPanelListProps {
  sources: SourceItem[];
}

const SourceChatPanelList: React.FC<SourceChatPanelListProps> = ({ sources }) => {
  const getSourceIcon = (source: SourceItem): string | null => {
    // Non-web sources should display "~" instead of an icon
    if (source.source_type !== 'web') {
      return null;
    }

    const url = source.metadata?.url;
    if (!url) return '/images/source-icon.webp';

    try {
      const urlObj = new URL(url);
      const domain = urlObj.hostname;

      // Special case for centralbank.ae
      if (domain.includes('centralbank.ae')) {
        return '/images/CBUAE.png';
      }

      // Use Google's favicon service
      return `https://www.google.com/s2/favicons?domain=${domain}&sz=64`;
    } catch (e) {
      return '/images/source-icon.webp';
    }
  };

  const getSourceName = (source: SourceItem): string => {
    // For global sources, show "Knowledge Base"
    if (source.source_type === 'global') {
      return 'Knowledge Base';
    }

    // For project sources, show "Project Knowledge Base"
    if (source.source_type === 'project') {
      return 'Project Knowledge Base';
    }

    // For documents, use filename (original file name)
    if (source.source_type === 'document' && source.metadata?.filename) {
      return source.metadata.filename;
    }

    // For API sources, use api_name
    if (source.source_type === 'api' && source.metadata?.api_name) {
      return source.metadata.api_name;
    }

    // For web sources, use domain
    if (source.source_type === 'web') {
      if (source.metadata?.domain) {
        return source.metadata.domain;
      }
      if (source.metadata?.url) {
        try {
          const urlObj = new URL(source.metadata.url);
          return urlObj.hostname.replace('www.', '');
        } catch (e) {
          // Fall through
        }
      }
    }

    // Fallback to source type
    return source.source_type;
  };

  const getSourceTitle = (source: SourceItem): string => {
    // Use filename as title for all source types that have it
    if (source.metadata?.filename) {
      return source.metadata.filename;
    }

    // Use API name for API sources
    if (source.source_type === 'api' && source.metadata?.api_name) {
      return source.metadata.api_name;
    }

    // Use web title if available
    if (source.source_type === 'web' && source.metadata?.title) {
      return source.metadata.title;
    }

    // For global/project without filename, use generic title
    if (source.source_type === 'global') {
      return 'Knowledge Base Source';
    }

    if (source.source_type === 'project') {
      return 'Project Document';
    }

    return `Source ${sources.indexOf(source) + 1}`;
  };

  const getSourceDetails = (source: SourceItem): string => {
    const details: string[] = [];

    // Add page number if available and meaningful (not page 1 or 0)
    const pageNum = source.metadata?.page_number;
    if (pageNum && pageNum > 1) {
      details.push(`p. ${pageNum}`);
    }

    // Add section if available
    if (source.metadata?.section) {
      details.push(source.metadata.section);
    }

    return details.join(' • ');
  };

  const handleViewSource = (url?: string) => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <div className="source-chat-panel-list">
      {sources.map((source, index) => {
        const sourceName = getSourceName(source);
        const sourceTitle = getSourceTitle(source);
        const sourceDetails = getSourceDetails(source);
        const hasUrl = !!source.metadata?.url;
        const iconUrl = getSourceIcon(source);

        // Check if this is a downloadable file
        const hasDownloadLink = (source.source_type === 'document' ||
                                  source.source_type === 'project' ||
                                  source.source_type === 'global') &&
                                 source.metadata?.filename;

        return (
          <div key={source.source_id || index} className="source-list-item">
            {/* Row 1: Reference Number + Filename (header) */}
            <div className="source-item-row-1">
              <span className="source-reference-value">{index + 1}</span>
              <span className="source-title">{sourceTitle}</span>
            </div>

            {/* Row 2: Text Snippet only (no page details) */}
            <div className="source-item-row-2">
              <p className="source-text">{source.content}</p>
            </div>

            {/* Row 3: Source Type (footer) + View Source (for web only) */}
            <div className="source-item-row-3">
              <div className="source-info-column">
                {iconUrl ? (
                  <img
                    src={iconUrl}
                    alt={sourceName}
                    className="source-item-icon"
                  />
                ) : (
                  <span className="source-item-icon-text">~</span>
                )}
                <span className="source-name">{sourceName}</span>
              </div>
              {hasUrl && source.source_type === 'web' && (
                <button
                  className="view-source-button"
                  onClick={() => handleViewSource(source.metadata?.url)}
                >
                  <span>View Source</span>
                  <ViewSourceIcon size={11} />
                </button>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default SourceChatPanelList;
